import { useState, useCallback, useEffect, useRef } from 'react';

export interface PaginationConfig<T> {
    initialPageSize?: number;
    initialPage?: number;
    pageSizeOptions?: number[];
    prefetchWindow?: number;
    enabled?: boolean;
}

export interface FetchParams {
    pageNumber: number;
    pageSize: number;
    [key: string]: any;
}

export interface FetchResponse<T> {
    data: T[];
    hasMore?: boolean;
}

export interface UsePrefetchPaginationReturn<T> {
    data: T[];
    currentPage: number;
    pageSize: number;
    loading: boolean;
    error: boolean;
    hasMore: boolean;
    lastKnownPage: number | null;
    knownPages: Set<number>;
    enoughItemsForPagination: boolean;

    goToPage: (page: number) => void;
    setPageSize: (size: number) => void;
    // `startPage` defaults to 1: callers that invalidated the cache (a link, a delete) ask for
    // the first page, while a caller restoring a position can name the page to land on.
    refresh: (startPage?: number) => void;
    refreshCurrentPage: () => void;
    prependData: (newData: T[]) => void;

    paginationProps: {
        currentPage: number;
        pageSize: number;
        hasNextPage: boolean;
        currentPageItemCount: number;
        onPageChange: (page: number) => void;
        onPageSizeChange: (size: number) => void;
        pageSizeOptions: number[];
        knownPages: Set<number>;
        lastKnownPage: number | null;
        enoughItemsForPagination: boolean;
    };
}

export function usePrefetchPagination<T>({
    fetchFn,
    filters = {},
    config = {},
}: {
    fetchFn: (params: FetchParams) => Promise<FetchResponse<T>>;
    filters?: Record<string, any>;
    config?: PaginationConfig<T>;
}): UsePrefetchPaginationReturn<T> {
    const {
        initialPageSize = 10,
        initialPage = 1,
        pageSizeOptions = [10, 20, 50, 100],
        prefetchWindow = 2,
        enabled = true,
    } = config;

    const smallestPageSize = Math.min(...pageSizeOptions);

    const hasUsedInitialPage = useRef(false);

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [pageSize, setPageSizeState] = useState(initialPageSize);
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastKnownPage, setLastKnownPage] = useState<number | null>(null);
    const [enoughItemsForPagination, setEnoughItemsForPagination] = useState(true);

    const [pageCache, setPageCache] = useState<Map<number, T[]>>(new Map());
    const [knownPages, setKnownPages] = useState<Set<number>>(new Set([1]));

    const buildPageRequests = useCallback(
        (
            requestPage: number,
        ): { requests: Promise<FetchResponse<T>>[]; requestedPages: number[] } => {
            const requests: Promise<FetchResponse<T>>[] = [];
            const requestedPages: number[] = [];

            // Fetch current page if not cached
            if (!pageCache.has(requestPage)) {
                requests.push(
                    fetchFn({
                        pageNumber: requestPage,
                        pageSize,
                        ...filters,
                    }),
                );
                requestedPages.push(requestPage);
            }

            // Prefetch next pages (N+1, N+2, etc.)
            for (let i = 1; i <= prefetchWindow; i++) {
                const nextPage = requestPage + i;
                const shouldFetch =
                    (lastKnownPage === null || nextPage <= lastKnownPage) &&
                    !pageCache.has(nextPage);

                if (shouldFetch) {
                    requests.push(
                        fetchFn({
                            pageNumber: nextPage,
                            pageSize,
                            ...filters,
                        }),
                    );
                    requestedPages.push(nextPage);
                }
            }

            return { requests, requestedPages };
        },
        [pageCache, lastKnownPage, pageSize, filters, fetchFn, prefetchWindow],
    );

    const prefetchPages = useCallback(
        async (requestPage: number) => {
            const requests: Promise<FetchResponse<T>>[] = [];
            const requestedPages: number[] = [];

            for (let i = 1; i <= prefetchWindow; i++) {
                const nextPage = requestPage + i;
                const shouldFetch =
                    (lastKnownPage === null || nextPage <= lastKnownPage) &&
                    !pageCache.has(nextPage);

                if (shouldFetch) {
                    requests.push(
                        fetchFn({
                            pageNumber: nextPage,
                            pageSize,
                            ...filters,
                        }),
                    );
                    requestedPages.push(nextPage);
                }
            }

            if (requests.length === 0) return;

            try {
                const responses = await Promise.allSettled(requests);
                const newCache = new Map(pageCache);
                const newKnownPages = new Set(knownPages);
                let newLastKnownPage = lastKnownPage;

                responses.forEach((response, index) => {
                    if (response.status === 'fulfilled') {
                        const pageData = response.value.data || [];
                        const pageNum = requestedPages[index];

                        if (pageData.length > 0) {
                            newCache.set(pageNum, pageData);
                            newKnownPages.add(pageNum);

                            if (
                                pageData.length < pageSize &&
                                (newLastKnownPage === null || pageNum > newLastKnownPage)
                            ) {
                                newLastKnownPage = pageNum;
                            }
                        } else if (newLastKnownPage === null || pageNum - 1 > newLastKnownPage) {
                            newLastKnownPage = pageNum - 1;
                        }
                    }
                });

                setPageCache(newCache);
                setKnownPages(newKnownPages);
                setLastKnownPage(newLastKnownPage);
            } catch (e) {
                console.error('Prefetch error:', e);
            }
        },
        [pageCache, knownPages, lastKnownPage, pageSize, filters, fetchFn, prefetchWindow],
    );

    const fetchPage = useCallback(
        async (targetPage: number, forceLoad = false) => {
            if (!forceLoad && pageCache.has(targetPage)) {
                const cachedPageData = pageCache.get(targetPage)!;
                setData(cachedPageData);
                setLoading(false);

                if (cachedPageData.length === 0) {
                    setHasMore(false);
                } else if (cachedPageData.length < pageSize) {
                    setHasMore(false);
                } else if (lastKnownPage !== null && targetPage >= lastKnownPage) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }

                prefetchPages(targetPage);
                return;
            }

            if (targetPage === 1) {
                setLoading(true);
            }

            try {
                const { requests, requestedPages } = buildPageRequests(targetPage);

                if (requests.length === 0) {
                    if (pageCache.has(targetPage)) {
                        setData(pageCache.get(targetPage)!);
                    }
                    setLoading(false);
                    return;
                }

                const responses = await Promise.allSettled(requests);

                const newCache = new Map(pageCache);
                const newKnownPages = new Set(knownPages);
                let newLastKnownPage = lastKnownPage;

                responses.forEach((response, index) => {
                    if (response.status === 'fulfilled') {
                        const pageData = response.value.data || [];
                        const pageNum = requestedPages[index];

                        if (pageData.length > 0) {
                            newCache.set(pageNum, pageData);
                            newKnownPages.add(pageNum);

                            if (
                                pageData.length < pageSize &&
                                (newLastKnownPage === null || pageNum > newLastKnownPage)
                            ) {
                                newLastKnownPage = pageNum;
                            }
                        }

                        if (pageNum === targetPage) {
                            setData(pageData);

                            if (pageData.length === 0) {
                                setHasMore(false);
                                newLastKnownPage = targetPage - 1;
                            } else if (pageData.length < pageSize) {
                                setHasMore(false);
                                newLastKnownPage = targetPage;
                            }
                        }

                        if (
                            pageData.length === 0 &&
                            (newLastKnownPage === null || pageNum - 1 > newLastKnownPage)
                        ) {
                            newLastKnownPage = pageNum - 1;
                        } else if (
                            pageData.length < pageSize &&
                            (newLastKnownPage === null || pageNum > newLastKnownPage)
                        ) {
                            newLastKnownPage = pageNum;
                        }
                    }
                });

                const currentPageData = newCache.get(targetPage);
                if (!currentPageData || currentPageData.length === 0) {
                    setHasMore(false);
                } else if (currentPageData.length < pageSize) {
                    setHasMore(false);
                } else if (newLastKnownPage !== null && targetPage >= newLastKnownPage) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }

                setPageCache(newCache);
                setKnownPages(newKnownPages);
                setLastKnownPage(newLastKnownPage);
                setLoading(false);
                setError(false);
            } catch (e) {
                console.error('Fetch error:', e);
                setData([]);
                setLoading(false);
                setError(true);
            }
        },
        [pageCache, knownPages, lastKnownPage, pageSize, buildPageRequests, prefetchPages],
    );

    const goToPage = useCallback(
        (page: number) => {
            setCurrentPage(page);
            fetchPage(page);
        },
        [fetchPage],
    );

    const changePageSize = useCallback(
        (size: number) => {
            if (size === pageSize) return;
            setPageSizeState(size);
            setCurrentPage(1);
            setPageCache(new Map());
            setKnownPages(new Set([1]));
            setLastKnownPage(null);
            setEnoughItemsForPagination(true);
            setLoading(true);
        },
        [pageSize],
    );

    const fetchWithFreshCache = async (startPage: number = 1) => {
        try {
            const requests: Promise<FetchResponse<T>>[] = [];
            const requestedPages: number[] = [];

            requests.push(
                fetchFn({
                    pageNumber: startPage,
                    pageSize,
                    ...filters,
                }),
            );
            requestedPages.push(startPage);

            for (let i = 1; i <= prefetchWindow; i++) {
                requests.push(
                    fetchFn({
                        pageNumber: startPage + i,
                        pageSize,
                        ...filters,
                    }),
                );
                requestedPages.push(startPage + i);
            }

            const responses = await Promise.allSettled(requests);

            const newCache = new Map();
            const newKnownPages = new Set([startPage]);
            let newLastKnownPage: number | null = null;

            responses.forEach((response, index) => {
                if (response.status === 'fulfilled') {
                    const pageData = response.value.data || [];
                    const pageNum = requestedPages[index];

                    if (pageData.length > 0) {
                        newCache.set(pageNum, pageData);
                        newKnownPages.add(pageNum);

                        if (
                            pageData.length < pageSize &&
                            (newLastKnownPage === null || pageNum > newLastKnownPage)
                        ) {
                            newLastKnownPage = pageNum;
                        }
                    } else if (newLastKnownPage === null || pageNum - 1 > newLastKnownPage) {
                        newLastKnownPage = pageNum - 1;
                    }

                    if (pageNum === startPage) {
                        setData(pageData);
                        // A short page hides the bar only while the list is on the smallest
                        // page size — on a bigger one the page-size select has to stay
                        // reachable, otherwise the list is stuck on that size (MP-15882).
                        setEnoughItemsForPagination(
                            pageSize !== smallestPageSize || pageData.length >= smallestPageSize,
                        );

                        if (pageData.length === 0) {
                            setHasMore(false);
                        } else if (pageData.length < pageSize) {
                            setHasMore(false);
                        } else {
                            setHasMore(true);
                        }
                    }
                }
            });

            setPageCache(newCache);
            setKnownPages(newKnownPages);
            setLastKnownPage(newLastKnownPage);
            setLoading(false);

            // `allSettled` swallows a rejection, so a failed request for the page being
            // shown would otherwise leave the previous rows on screen — presenting them as
            // the result of a query that never succeeded.
            if (responses[0]?.status === 'rejected') {
                setData([]);
                setHasMore(false);
                setError(true);
            } else {
                setError(false);
            }
        } catch (e) {
            console.error('Fetch error:', e);
            setData([]);
            setLoading(false);
            setError(true);
        }
    };

    const refresh = useCallback(
        async (startPage: number = 1) => {
            setCurrentPage(startPage);
            setPageCache(new Map());
            setKnownPages(new Set([startPage]));
            setLastKnownPage(null);
            setHasMore(true);
            setLoading(true);

            fetchWithFreshCache(startPage);
        },
        [fetchFn, pageSize, filters, prefetchWindow],
    );

    const refreshCurrentPage = useCallback(async () => {
        setLoading(true);

        try {
            const response = await fetchFn({
                pageNumber: currentPage,
                pageSize,
                ...filters,
            });
            const pageData = response.data || [];

            setData(pageData);
            setPageCache(new Map([[currentPage, pageData]]));
            setHasMore(pageData.length >= pageSize);
            setLoading(false);
            setError(false);
        } catch (e) {
            console.error('Refresh error:', e);
            setLoading(false);
            setError(true);
        }
    }, [currentPage, fetchFn, pageSize, filters]);

    const prependData = useCallback(
        (newData: T[]) => {
            if (currentPage === 1 && newData.length > 0) {
                setData((prevData) => [...newData, ...prevData]);
                setPageCache((prevCache) => {
                    const newCache = new Map(prevCache);
                    const page1Data = newCache.get(1) || [];
                    newCache.set(1, [...newData, ...page1Data]);
                    return newCache;
                });
            }
        },
        [currentPage],
    );

    useEffect(() => {
        if (enabled === false) return;
        if (!hasUsedInitialPage.current) {
            hasUsedInitialPage.current = true;
            refresh(initialPage);
        } else {
            refresh(1);
        }
        // eslint-disable-next-line
    }, [pageSize, JSON.stringify(filters), enabled]);

    return {
        data,
        currentPage,
        pageSize,
        loading,
        error,
        hasMore,
        lastKnownPage,
        knownPages,
        enoughItemsForPagination,
        goToPage,
        setPageSize: changePageSize,
        refresh,
        refreshCurrentPage,
        prependData,

        paginationProps: {
            currentPage,
            pageSize,
            hasNextPage: hasMore,
            currentPageItemCount: data.length,
            onPageChange: goToPage,
            onPageSizeChange: changePageSize,
            pageSizeOptions,
            knownPages,
            lastKnownPage,
            enoughItemsForPagination,
        },
    };
}
